import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { fetchStatementSalesDetails, fetchStatementLayawayDetails, fetchStatementExpenseDetails } from '@/api';
import { format } from 'date-fns';

interface StatementDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'sales' | 'layaway' | 'expense';
  startDate: string;
  endDate: string;
  label: string;
  categoryId?: string;
  categoryName?: string;
  currencySymbol: string;
}

interface SaleDetail {
  id: string;
  invoice_number: number;
  total_amount: number;
  total_returned_amount: number;
  net_amount: number;
  created_at: string;
  branch_name?: string;
}

interface LayawayDetail {
  id: string;
  amount: number;
  payment_date: string;
  layaway_number: number;
  branch_name?: string;
}

interface ExpenseDetail {
  id: string;
  title: string;
  amount: number;
  expense_date: string;
  category_name: string;
  branch_name?: string;
  user_name?: string;
}

export function StatementDetailDialog({
  open,
  onOpenChange,
  type,
  startDate,
  endDate,
  label,
  categoryId,
  categoryName,
  currencySymbol,
}: StatementDetailDialogProps) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<(SaleDetail | LayawayDetail | ExpenseDetail)[]>([]);

  useEffect(() => {
    if (!open) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        let response;
        if (type === 'sales') {
          response = await fetchStatementSalesDetails(startDate, endDate);
        } else if (type === 'layaway') {
          response = await fetchStatementLayawayDetails(startDate, endDate);
        } else if (type === 'expense') {
          response = await fetchStatementExpenseDetails(startDate, endDate, categoryId);
        }
        setDetails(response.data.details || []);
      } catch (error) {
        console.error('Failed to fetch details:', error);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [open, type, startDate, endDate, categoryId]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={type === 'expense' ? 5 : 4} className="text-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </TableCell>
        </TableRow>
      );
    }

    if (details.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={type === 'expense' ? 5 : 4} className="text-center py-8 text-muted-foreground">
            No transactions found for this period
          </TableCell>
        </TableRow>
      );
    }

    if (type === 'sales') {
      return (details as SaleDetail[]).map((sale) => (
        <TableRow key={sale.id}>
          <TableCell>Invoice #{sale.invoice_number}</TableCell>
          <TableCell>{sale.branch_name || 'N/A'}</TableCell>
          <TableCell>{formatDate(sale.created_at)}</TableCell>
          <TableCell className="text-right font-medium">
            {currencySymbol}{Number(sale.net_amount).toFixed(2)}
          </TableCell>
        </TableRow>
      ));
    }

    if (type === 'layaway') {
      return (details as LayawayDetail[]).map((payment) => (
        <TableRow key={payment.id}>
          <TableCell>Layaway #{payment.layaway_number}</TableCell>
          <TableCell>{payment.branch_name || 'N/A'}</TableCell>
          <TableCell>{formatDate(payment.payment_date)}</TableCell>
          <TableCell className="text-right font-medium">
            {currencySymbol}{Number(payment.amount).toFixed(2)}
          </TableCell>
        </TableRow>
      ));
    }

    if (type === 'expense') {
      return (details as ExpenseDetail[]).map((expense) => (
        <TableRow key={expense.id}>
          <TableCell>{expense.title}</TableCell>
          <TableCell>{expense.category_name}</TableCell>
          <TableCell>{expense.branch_name || 'N/A'}</TableCell>
          <TableCell>{formatDate(expense.expense_date)}</TableCell>
          <TableCell className="text-right font-medium">
            {currencySymbol}{Number(expense.amount).toFixed(2)}
          </TableCell>
        </TableRow>
      ));
    }

    return null;
  };

  const getTotalAmount = () => {
    if (details.length === 0) return 0;

    if (type === 'sales') {
      return (details as SaleDetail[]).reduce((sum, sale) => sum + Number(sale.net_amount), 0);
    }
    if (type === 'layaway') {
      return (details as LayawayDetail[]).reduce((sum, payment) => sum + Number(payment.amount), 0);
    }
    if (type === 'expense') {
      return (details as ExpenseDetail[]).reduce((sum, expense) => sum + Number(expense.amount), 0);
    }
    return 0;
  };

  const getDialogTitle = () => {
    if (type === 'expense' && categoryName) {
      return `${categoryName} - ${label}`;
    }
    return label;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                {type === 'sales' && (
                  <>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </>
                )}
                {type === 'layaway' && (
                  <>
                    <TableHead>Layaway</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </>
                )}
                {type === 'expense' && (
                  <>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>{renderTableContent()}</TableBody>
          </Table>
        </div>

        {!loading && details.length > 0 && (
          <div className="mt-4 pt-4 border-t flex justify-between items-center shrink-0">
            <div className="text-sm text-muted-foreground">
              Total: {details.length} transaction{details.length !== 1 ? 's' : ''}
            </div>
            <div className="text-lg font-semibold">
              Total: {currencySymbol}{getTotalAmount().toFixed(2)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
